<template>
  <TemplateBase :page="page" class="bg-gradient-to-r from-app-secondary-2-yellow--dark-portica to-app-secondary-2-orange--mango-tango">
    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="true" />
    </template>

    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" />
    </template>

    <div class="grid grid-cols-12 relative">
      <section v-if="page.image" class="md:hidden col-span-full row-start-1 bg-transparent z-0">
        <MoleculeMaskVisual :image="page.image" :shape-number="7" />
      </section>
      <section v-if="page.image" class="hidden md:block absolute w-full bg-transparent z-0">
        <MoleculeMaskVisual :image="page.image" :shape-number="6" />
      </section>
      <div class="absolute top-0 bottom-0 left-0 right-0 opacity-50 v-gradient-bg v-gradient-bg--gold v-gradient-bg--lyre-only z-1"></div>
      <div class="col-span-full container z-grid v-page-section-container-padding mt-6 mb-16 md:pt-16 xl:pt-20">
        <div class="relative z-20 col-span-full lg:col-start-2 md:col-end-9 xl:col-end-8">
          <h1 class="text-app-primary-1-red--fire-brick leading-tight font-medium text-3xl md:text-5xl mb-1 md:mb-2">
            {{ page.title }}
          </h1>
          <div v-if="page.introduction" class="text-base font-normal mb-6 md:mb-8" v-html="stripTags(page.introduction)" />
        </div>

        <div class="relative z-10 col-span-full lg:col-start-2 lg:col-end-12 z-grid pt-5 pb-4 md:pt-6 border-y border-app-primary-grey--black-squeeze">
          <!-- start search box -->
          <div class="mb-5 md:mt-1 col-span-full md:col-span-6 lg:col-span-4">
            <MoleculeInputWithIcon
              ref="search"
              :value="keyword"
              :placeholder="`${$t('discovery-search-overview.search-input-placeholder')}`"
              icon="search"
              @input="(value) => setKeyword(value)"
            />
          </div>
          <!-- end search box -->

          <!-- start filters -->
          <div class="col-span-full lg:col-span-8">
            <ul class="flex flex-wrap">
              <!-- currently only one facet is available "discoveryCollections", but can be more in the future -->
              <li v-for="{ values, key } in facets" :key="key" class="relative z-10 mr-4">
                <!-- manually load label for each facet for now -->
                <span v-if="key === 'categories'" class="block text-base font-normal mb-2 text-app-primary-1-red--fire-brick">{{
                  $t('discovery-search-overview.filter-by-category')
                }}</span>
                <!-- facet "categories" should be selectable at all times -->
                <ul class="flex flex-row flex-wrap w-full">
                  <li v-for="value in values" :key="value.value" class="mr-4 mb-2">
                    <MoleculeButtonSearchFilter :state="getState(value.active)" @click="enableFacet({ name: key, value: value.value })">
                      {{ value.label || t_(value.value) }}
                    </MoleculeButtonSearchFilter>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        <!-- end filters -->

        <!-- start selected filters -->
        <div class="relative z-10 col-span-full lg:col-start-2 lg:col-end-12 mb-8 md:mb-12 pt-5 pb-4 md:pt-6 mb:pb-5">
          <div v-if="keyword !== '' || activeTagFacets.length > 0 || activeRegularFacets.length > 0" class="col-span-full flex">
            <div class="text-sm leading-none flex items-center flex-wrap">
              <template v-if="keyword !== ''">
                <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-search-keywords') }}</p>
                <div class="mr-2 mb-1">
                  <MoleculeFacet :title="keyword" @click="() => setKeyword('')">
                    <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
                  </MoleculeFacet>
                </div>
              </template>
              <!-- Active filters with tag -->
              <template v-if="activeTagFacets.length > 0">
                <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-tags') }}</p>
                <div v-for="{ facet, label, value } in activeTagFacets" :key="value" class="mr-2 mb-1">
                  <MoleculeFacet :title="label || t_(value)" @click="resetFacet({ name: facet })">
                    <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
                  </MoleculeFacet>
                </div>
              </template>
              <template v-if="activeRegularFacets.length > 0">
                <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-filters') }}</p>
                <div v-for="{ facet, label, value } in activeRegularFacets" :key="value" class="mr-2 mb-1">
                  <MoleculeFacet :title="label || t_(value)" @click="resetFacet({ name: facet })">
                    <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
                  </MoleculeFacet>
                </div>
              </template>
              <!-- Reset filter link -->
              <p v-if="keyword !== '' || activeRegularFacets.length > 0" class="underline cursor-pointer font-medium mb-1" @click="reset()">
                {{ $t('filters.remove-filters') }}
              </p>
            </div>
          </div>
        </div>
        <!-- end selected filters -->

        <!-- start story results + navigation -->
        <section v-if="discoveries.length" class="relative z-10 col-span-full mb-8 md:mb-12">
          <ul class="col-span-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-8">
            <li v-for="(collection, index) of discoveries" :key="index" class="flex">
              <MoleculeCardOverview
                :title="collection.title"
                :subtitle="collection.subtitle"
                :image="collection.teaserImage"
                :url="collection.url"
                :icon="get(collection, 'discoveryCategory.icon.slug')"
                background-color="v-gradient-bg  v-gradient-bg--gold v-gradient-bg--gradient-only"
                type="discover"
              />
            </li>
          </ul>
        </section>

        <section v-if="discoveries.length === 0" class="relative z-10 col-span-full lg:col-start-2 lg:col-end-12">
          <p>{{ $t('discovery-search-overview.no-results') }}</p>
        </section>

        <OrganismPagination :count="totalHits" :limit="hitsPerPage" :page="currentPage" class="col-span-full mt-1 mb-16" @click="({ page: newPage }) => setCurrentPage(newPage)" />
        <!-- end story results + navigation -->
      </div>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { stripTags } from '@zicht/nuxt-util'

defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const { t_ } = setupTranslationPrefixer('DiscoverySearchOverview')
const getState = (active: boolean) => (active ? 'active' : undefined)

const route = useRoute()
const router = useRouter()
const discoveryOverviewStore = useDiscoveryOverviewStore()

const activeTagFacets = computed(() => discoveryOverviewStore.activeTagFacets)
const activeRegularFacets = computed(() => discoveryOverviewStore.activeRegularFacets)
const currentPage = computed(() => discoveryOverviewStore.page)
const discoveries = computed(() => discoveryOverviewStore.discoveries)
const facets = computed(() => discoveryOverviewStore.facets)
const hitsPerPage = computed(() => discoveryOverviewStore.hitsPerPage)
const keyword = computed(() => discoveryOverviewStore.keyword)
const totalHits = computed(() => discoveryOverviewStore.totalHits)
const urlParameters = computed(() => discoveryOverviewStore.urlParameters)

function reset() {
  discoveryOverviewStore.reset()
}
function setCurrentPage(newPage: number) {
  discoveryOverviewStore.setPage(newPage)
}
function setUrlParameters(query: CmsData) {
  discoveryOverviewStore.setUrlParameters(query)
}
function setKeyword(newKeyword: string) {
  discoveryOverviewStore.setKeyword(newKeyword)
}
function enableFacet(facet: { name: string; value: string }) {
  discoveryOverviewStore.enableFacet(facet)
}
function resetFacet(facet: { name: string }) {
  discoveryOverviewStore.resetFacet(facet)
}

watch(
  () => route.query,
  (query) => {
    if (!routerQueryIsEqual(urlParameters.value, query)) {
      setUrlParameters(query)
    }
  },
)
watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})
</script>
